/**
 * useDocViewDraft — the view builder's backing for an in-DOCUMENT ```view block.
 *
 * The HUD builder persists to localStorage drafts + `.arch` via useViewDrafts;
 * a doc-embedded view instead lives as TEXT in the markdown block, so this hook
 * keeps the editable rows and writes every change straight back into the block
 * (a CM6 transaction, supplied as `onWrite`). It exposes exactly the surface
 * `ViewClauseComposer` needs (rows / name / errors + onChangeRows / onName), so
 * the SAME composer drives both.
 *
 * Seeded ONCE from the block text (parse → `viewDeclToDraft`); subsequent block
 * text is NOT re-read — while the panel is open its own writes are the source of
 * truth, so re-seeding would clobber an in-progress edit with its round-tripped
 * echo (the MarkdownEditor "lastSent" idiom).
 */
import { useCallback, useMemo, useState } from "react";
import { parseViewSource } from "@archlang/engine";
import { normalizeShowAll, seedShowAll, type ClauseRow } from "./clauseModel.js";
import {
  compileDraft,
  draftClauseTexts,
  UNNAMED_DRAFT_NAME,
  type ViewDraft,
} from "./useViewDrafts.js";
import { sliceSourceFrom, viewDeclToDraft } from "./viewDeclToDraft.js";

/** The synthetic file the block text parses under (spans key off it, so the
 *  raw-clause slicer reads back from the SAME text). */
const DOC_VIEW_PATH = "/__doc_view__.arch";

/** Render a draft back to `view Name { … }` block text (the inverse of the seed;
 *  matches useViewDrafts.compileDraft's assembly so it round-trips). */
export function serializeViewDraft(draft: ViewDraft): string {
  const name = draft.name.trim() || UNNAMED_DRAFT_NAME;
  const body = draftClauseTexts(draft);
  return `view ${name} {\n${body.map((c) => `    ${c}`).join("\n")}\n}`;
}

/** Parse the block text into editable rows + name; a malformed (mid-edit) block
 *  falls back to a fresh show-all so the builder still opens. */
function seedDraft(blockText: string): ViewDraft {
  const r = parseViewSource(blockText, DOC_VIEW_PATH);
  if (r.errors.length > 0) return { name: r.decl?.name ?? "", rows: [seedShowAll()] };
  return viewDeclToDraft(r.decl, sliceSourceFrom(new Map([[DOC_VIEW_PATH, blockText]])));
}

export interface DocViewDraftApi {
  readonly rows: readonly ClauseRow[];
  readonly name: string;
  readonly errors: readonly string[];
  readonly onChangeRows: (rows: readonly ClauseRow[]) => void;
  readonly onName: (name: string) => void;
}

export function useDocViewDraft(
  blockText: string,
  onWrite: (text: string) => void,
): DocViewDraftApi {
  const [draft, setDraft] = useState<ViewDraft>(() => seedDraft(blockText));
  const errors = useMemo(() => compileDraft(draft).errors, [draft]);

  // Every mutation normalises `show *` (drop when a specific show is added,
  // restore when none remain), updates local rows, and writes the block.
  const commit = useCallback(
    (next: ViewDraft): void => {
      const normalized: ViewDraft = { ...next, rows: normalizeShowAll(next.rows) };
      setDraft(normalized);
      onWrite(serializeViewDraft(normalized));
    },
    [onWrite],
  );

  return useMemo<DocViewDraftApi>(
    () => ({
      rows: draft.rows,
      name: draft.name,
      errors,
      onChangeRows: (rows) => commit({ ...draft, rows: [...rows] }),
      onName: (name) => commit({ ...draft, name }),
    }),
    [draft, errors, commit],
  );
}
